/*
 * Guillo AI assistant — dock controller, response validation, module
 * renderer, journey + history management.
 *
 * Architecture:
 *   prompt → provider (configured backend endpoint, or the separated
 *   development provider in gassistant-provider-dev.js) → strict JSON
 *   contract → validation (module types, product objects, counts, text
 *   lengths, asset registry ids) → renderer builds APPROVED module DOM
 *   only → scenes mount into the journey stage → the existing scene
 *   controller (gscenes.js) drives navigation via enterJourney().
 *
 * No model is called from this file. No model-generated HTML is ever
 * injected: all module structures are built here from validated data.
 * Cart actions use the existing GuilloCart drawer (single cart state).
 */
(function () {
  'use strict';

  var MAX_MODULES = 6;
  var MAX_RECOMMENDATIONS = 3;
  var MAX_FOLLOW_UPS = 4;
  var MAX_TEXT = 140;
  var REQUEST_TIMEOUT_MS = 10000;

  var MODULE_TYPES = [
    'editorial_result_intro', 'product_grid_three_column', 'campaign_full_bleed',
    'product_focus', 'product_detail', 'product_comparison', 'complete_look',
    'policy_answer', 'direct_navigation', 'fallback'
  ];

  var PLACEHOLDERS = [
    'What are you dressing for?',
    'Show me outfits for dinner',
    'Build me a travel wardrobe',
    'Outfits for the office',
    'What goes with sage chinos',
    'Dress me for the weekend'
  ];

  var TOP_SIZES = ['S', 'M', 'L', 'XL', 'XXL'];
  var WAIST_SIZES = ['28', '30', '32', '34', '36'];
  function normSize(t) { return String(t || '').replace(/[^0-9a-zA-Z]/g, '').toUpperCase(); }

  var LOADING_LINES = [
    'Considering your request',
    'Exploring the collection',
    'Building your selection',
    'Finding the right fit'
  ];

  function $(sel, root) { return (root || document).querySelector(sel); }
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text) n.textContent = text;
    return n;
  }
  function money(cents) {
    try {
      var fmt = (window.theme && window.theme.moneyFormat) || '${{amount}}';
      var m = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
      var key = m ? m[1] : 'amount';
      var v = cents / 100;
      var out;
      if (key === 'amount_no_decimals' || key === 'amount_no_decimals_with_comma_separator') {
        out = Math.round(v).toLocaleString('en-AU');
      } else if (key === 'amount_with_comma_separator') {
        out = v.toFixed(2).replace('.', ',');
      } else {
        out = v.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      return fmt.replace(/\{\{\s*\w+\s*\}\}/, out).replace(/<[^>]*>/g, '');
    } catch (e) { return '$' + Math.round(cents / 100); }
  }
  function clampText(s) { return typeof s === 'string' ? s.slice(0, MAX_TEXT) : ''; }

  function GAssistant() {
    this.root = $('[data-gassistant-root]');
    if (!this.root) return;
    this.dock = $('[data-gdock]', this.root);
    this.input = $('[data-gdock-input]', this.root);
    this.tray = $('[data-gdock-tray]', this.root);
    this.trayBody = $('[data-gdock-tray-body]', this.root);
    this.chips = $('[data-gdock-chips]', this.root);
    this.loading = $('[data-gloading]', this.root);
    this.journeyEl = $('[data-gjourney]', this.root);
    this.journeySizes = $('[data-gjourney-sizes]', this.root);
    this.stage = $('[data-gjourney-stage]', this.root);
    this.history = $('[data-ghistory]', this.root);
    this.scrollHint = $('[data-gdock-scrollhint]', this.root);
    this.upHint = $('[data-gdock-uphint]', this.root);
    this.suggestToggle = $('[data-gdock-suggest-toggle]', this.root);

    try {
      this.assets = JSON.parse($('[data-gassistant-assets]').textContent);
    } catch (e) { this.assets = {}; }
    var self0 = this;
    // Registry hygiene: drop entries whose file reference failed to resolve
    // to a CDN asset, then harvest the homepage's own (merchant-assigned)
    // scene media as additional approved assets.
    Object.keys(this.assets).forEach(function (id) {
      var u = self0.assets[id] && self0.assets[id].url;
      if (!u || !/cdn\.shopify|\/videos\/|\/files\//.test(u)) delete self0.assets[id];
    });
    document.querySelectorAll('.main-content [data-gscene] video source[data-src], .main-content [data-gscene] video source[src]')
      .forEach(function (s, i) {
        var u = s.getAttribute('data-src') || s.getAttribute('src');
        if (u) self0.assets['home-video-' + i] = { kind: 'video', orient: 'portrait', tags: ['campaign', 'lifestyle'], url: u };
      });
    var heroImg = document.querySelector('.main-content .gscene--welcome .gs-media img');
    if (heroImg && heroImg.currentSrc || heroImg && heroImg.src) {
      this.assets['home-hero'] = { kind: 'image', orient: 'landscape', tags: ['campaign', 'welcome', 'travel', 'lifestyle'], url: heroImg.currentSrc || heroImg.src };
    }

    this.endpoint = this.root.getAttribute('data-endpoint') || '';
    try { this.sizes = JSON.parse(window.localStorage.getItem('gassistant-sizes')) || null; }
    catch (e) { this.sizes = null; }
    try {
      var cfgEl = document.querySelector('[data-gassistant-outfits]');
      this.outfitConfig = cfgEl ? JSON.parse(cfgEl.textContent) : null;
    } catch (e) { this.outfitConfig = null; }
    try {
      var sgEl = document.querySelector('[data-gassistant-sizeguides]');
      this.sizeGuides = sgEl ? JSON.parse(sgEl.textContent) : null;
    } catch (e) { this.sizeGuides = null; }
    this.pendingPrompt = null;
    this.offer = {
      on: this.root.getAttribute('data-offer-enable') === 'true',
      min: parseInt(this.root.getAttribute('data-offer-min') || '3', 10),
      pct: parseInt(this.root.getAttribute('data-offer-pct') || '20', 10),
      label: this.root.getAttribute('data-offer-label') || 'Build Your Wardrobe'
    };
    this.journey = null;          // current validated journey
    this.journeys = this.load();  // session history
    this.abort = null;
    this.loadTimer = null;
    this.phTimer = null;
    this.sr = el('div', 'gsr-only');
    this.sr.setAttribute('aria-live', 'polite');
    document.body.appendChild(this.sr);

    this.bind();
    this.root.hidden = false;
    this.rotatePlaceholders();
    this.syncSceneContext(0);

    // Deep links:
    //   /?assistant=outfit                       menu "Build an outfit"
    //   /?assistant=stylist&q=...&anchor=handle  product-page AI launcher
    // Unknown values just focus the input.
    var deepLink = null, deepQ = '', deepAnchor = '';
    try {
      var qp = new URLSearchParams(window.location.search);
      deepLink = qp.get('assistant');
      deepQ = qp.get('q') || '';
      deepAnchor = qp.get('anchor') || '';
    } catch (e) { /* old browser */ }
    this.anchor = null;
    if (deepLink) {
      var self2 = this;
      window.setTimeout(function () {
        if (deepLink === 'outfit') {
          self2.input.value = 'Build me an outfit';
          self2.ask('Build me an outfit');
        } else if (deepLink === 'stylist' && deepQ) {
          if (/^[a-z0-9-]+$/.test(deepAnchor)) self2.anchor = deepAnchor;
          self2.input.value = deepQ.slice(0, 190);
          self2.ask(self2.input.value);
        } else {
          self2.input.focus();
        }
      }, 700);
    }
  }

  // ------------------------------------------------------------ bindings

  GAssistant.prototype.bind = function () {
    var self = this;

    this.dock.addEventListener('submit', function (e) {
      e.preventDefault();
      var q = (self.input.value || '').trim().slice(0, 190);
      if (q) self.ask(q);
    });

    $('[data-gdock-plus]', this.root).addEventListener('click', function () {
      self.toggleChips();
    });
    this.shortcuts = $('[data-gdock-shortcuts]', this.root);
    if (this.shortcuts) {
      this.shortcuts.addEventListener('click', function (e) {
        var b = e.target.closest('[data-gdock-shortcut]');
        if (!b) return;
        var q = b.getAttribute('data-gdock-shortcut');
        self.input.value = q;
        self.ask(q);
      });
    }
    var sizesChip = $('[data-gdock-sizes]', this.root);
    if (sizesChip) {
      sizesChip.addEventListener('click', function () { self.sizeTray(); });
      this.renderSizesChip();
    }
    this.suggestToggle.addEventListener('click', function () { self.toggleChips(); });

    this.chips.addEventListener('click', function (e) {
      var b = e.target.closest('button');
      if (b) { self.input.value = b.textContent; self.ask(b.textContent); }
    });

    if (this.journeySizes) {
      this.journeySizes.addEventListener('click', function () { self.sizeTray(); });
    }
    $('[data-gdock-tray-close]', this.root).addEventListener('click', function () { self.closeTray(); });
    $('[data-gjourney-close]', this.root).addEventListener('click', function () { self.closeJourney(); });
    $('[data-ghistory-close]', this.root).addEventListener('click', function () { self.history.hidden = true; });
    $('[data-ghistory-clear]', this.root).addEventListener('click', function () {
      self.journeys = [];
      self.save();
      self.renderHistory();
    });
    $('[data-ghistory-list]', this.root).addEventListener('click', function (e) {
      var b = e.target.closest('button[data-journey-id]');
      if (!b) return;
      var j = self.journeys.filter(function (x) { return x.id === b.getAttribute('data-journey-id'); })[0];
      if (j) { self.history.hidden = true; self.mount(j); }
    });

    document.addEventListener('gassistant:history', function () {
      self.renderHistory();
      self.history.hidden = !self.history.hidden;
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (!self.history.hidden) { self.history.hidden = true; return; }
      if (!self.tray.hidden) { self.closeTray(); return; }
      if (self.journey && self.journeyEl && !self.journeyEl.hidden) self.closeJourney();
    });

    // contextual controls per scene
    document.addEventListener('gscene:change', function (e) {
      self.syncSceneContext(e.detail.index, e.detail.scene);
    });

    // journey module delegated clicks
    this.stage.addEventListener('click', function (e) { self.onStageClick(e); });

    // when the product-focus tray closes, the dock returns
    document.addEventListener('gfocus:trayclosed', function () { self.dock.hidden = false; });

    // tagged products on campaign media open the product tray
    document.addEventListener('click', function (e) {
      var tag = e.target.closest && e.target.closest('[data-gtag-product]');
      if (!tag) return;
      var dataEl = tag.parentElement.querySelector('[data-gtag-data]');
      if (!dataEl) return;
      try {
        var product = JSON.parse(dataEl.textContent);
        self.productTray(product);
        if (window.innerWidth >= 900) self.tray.classList.add('gdock__tray--side');
      } catch (err) {
        if (product && product.url) window.location.href = product.url;
      }
    });
    document.addEventListener('gfocus:trayopened', function () { self.dock.hidden = true; });
  };

  GAssistant.prototype.syncSceneContext = function (index, scene) {
    var isJourney = this.journey && !this.journeyEl.hidden;
    // the product-focus scene replaces the dock with its own tray
    var focus = scene && scene.querySelector && scene.querySelector('[data-gfocus]');
    var focusActive = focus && !focus.classList.contains('gfocus--tray-closed');
    this.dock.hidden = !!focusActive;
    var journeyHasMore = isJourney && this.stage.children.length > 1;
    this.scrollHint.hidden = !(index === 0 && (!isJourney || journeyHasMore));
    if (this.upHint) this.upHint.hidden = !(index > 0);
    var isCampaign = scene && scene.querySelector &&
      scene.querySelector('.gscene--portrait, .gmod--bleed, .gmod--intro');
    // scenes with their own action pill keep it unobstructed; follow-up
    // suggestions stay reachable through the dock's plus button
    var hasOwnCta = scene && scene.querySelector &&
      scene.querySelector('.gportrait__pill, .gmod__pill-row');
    this.suggestToggle.hidden = !(isCampaign && !hasOwnCta && this.currentFollowUps().length);
    if (this.chips && !this.chips.hidden) this.chips.hidden = true;
  };

  GAssistant.prototype.currentFollowUps = function () {
    if (this.journey && this.journey.follow_up_prompts) return this.journey.follow_up_prompts;
    return ['Show me what’s new', 'Find something for travel', 'Compare your chino fits'];
  };

  GAssistant.prototype.renderSizesChip = function () {
    var chip = $('[data-gdock-sizes]', this.root);
    if (!chip) return;
    chip.hidden = false;
    chip.textContent = this.sizes
      ? (this.sizes.top || '–') + ' · ' + (this.sizes.waist || '–') + '"'
      : 'Size';
  };

  // quiet reminder on journey screens that results are sized to the customer;
  // tapping it reopens the size tray
  GAssistant.prototype.renderJourneySizes = function () {
    if (!this.journeySizes) return;
    if (this.sizes && (this.sizes.top || this.sizes.waist)) {
      var parts = [];
      if (this.sizes.top) parts.push(this.sizes.top);
      if (this.sizes.waist) parts.push(this.sizes.waist + '"');
      this.journeySizes.textContent = 'Sized for you · ' + parts.join(' · ');
    } else {
      this.journeySizes.textContent = 'Add your sizes';
    }
    this.journeySizes.hidden = false;
  };

  // family-tabbed measurement tables (mirrors the product-page guides)
  GAssistant.prototype.guidePanel = function (kind) {
    var guides = (this.sizeGuides && this.sizeGuides[kind]) || [];
    var panel = el('div', 'gsize__panel');
    if (!guides.length) {
      panel.appendChild(el('p', 'gsize__guide', 'Exact garment measurements are on every product page.'));
      return panel;
    }
    var tabs = el('div', 'gsize__tabs');
    var tableWrap = el('div', 'gsize__tablewrap');
    var render = function (g) {
      tableWrap.innerHTML = '';
      var t = document.createElement('table');
      t.className = 'gsize__table';
      g.rows.forEach(function (row, ri) {
        var tr = document.createElement('tr');
        row.forEach(function (cell) {
          var td = document.createElement(ri === 0 ? 'th' : 'td');
          td.textContent = cell;
          tr.appendChild(td);
        });
        t.appendChild(tr);
      });
      tableWrap.appendChild(t);
      tableWrap.appendChild(el('p', 'gsize__unit', 'Measurements in ' + (g.unit || 'cm') + '. Full guides on each product page.'));
    };
    guides.forEach(function (g, i) {
      var b = el('button', '', g.title);
      b.type = 'button';
      b.setAttribute('aria-pressed', i === 0 ? 'true' : 'false');
      b.addEventListener('click', function () {
        tabs.querySelectorAll('button').forEach(function (x) { x.setAttribute('aria-pressed', 'false'); });
        b.setAttribute('aria-pressed', 'true');
        render(g);
      });
      tabs.appendChild(b);
    });
    render(guides[0]);
    panel.appendChild(tabs);
    panel.appendChild(tableWrap);
    return panel;
  };

  // capture top + waist sizes; runs the pending outfit query on confirm
  GAssistant.prototype.sizeTray = function () {
    var self = this;
    this.trayBody.innerHTML = '';
    this.trayBody.appendChild(el('p', 'gtray__title', 'Your sizes'));
    var current = this.sizes || {};

    var mkRow = function (label, values, key, guide) {
      var wrap = el('div', 'gtray__outfit-item');
      var head = el('p', 'gtray__outfit-name', label);
      var g = el('button', 'gsize__guide-toggle', 'Size guide');
      g.type = 'button';
      head.appendChild(g);
      wrap.appendChild(head);
      var sizes = el('div', 'gtray__sizes');
      values.forEach(function (v) {
        var b = el('button', '', key === 'waist' ? v + '"' : v);
        b.type = 'button';
        b.setAttribute('aria-pressed', current[key] === v ? 'true' : 'false');
        b.addEventListener('click', function () {
          sizes.querySelectorAll('button').forEach(function (x) { x.setAttribute('aria-pressed', 'false'); });
          b.setAttribute('aria-pressed', 'true');
          current[key] = v;
        });
        sizes.appendChild(b);
      });
      wrap.appendChild(sizes);
      var guideEl = self.guidePanel(guide);
      guideEl.hidden = true;
      g.addEventListener('click', function () { guideEl.hidden = !guideEl.hidden; });
      wrap.appendChild(guideEl);
      return wrap;
    };

    this.trayBody.appendChild(mkRow('Top size', TOP_SIZES, 'top', 'top'));
    this.trayBody.appendChild(mkRow('Trouser waist', WAIST_SIZES, 'waist', 'waist'));

    var actions = el('div', 'gtray__actions');
    var confirm = el('button', 'gtray__atc', this.pendingPrompt ? 'Show my outfits' : 'Save sizes');
    confirm.type = 'button';
    confirm.addEventListener('click', function () {
      self.sizes = { top: current.top || '', waist: current.waist || '' };
      try { window.localStorage.setItem('gassistant-sizes', JSON.stringify(self.sizes)); } catch (e) { /* private mode */ }
      self.renderSizesChip();
      self.renderJourneySizes();
      self.closeTray();
      if (self.pendingPrompt) {
        var p = self.pendingPrompt;
        self.pendingPrompt = null;
        self.ask(p);
      }
    });
    actions.appendChild(confirm);
    this.trayBody.appendChild(actions);
    this.openTray();
  };

  GAssistant.prototype.toggleChips = function () {
    var prompts = this.currentFollowUps();
    this.chips.innerHTML = '';
    var self = this;
    prompts.slice(0, MAX_FOLLOW_UPS).forEach(function (p) {
      self.chips.appendChild(el('button', '', p));
    });
    this.chips.hidden = !this.chips.hidden;
  };

  GAssistant.prototype.rotatePlaceholders = function () {
    var self = this, i = 0;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.phTimer = window.setInterval(function () {
      if (document.activeElement === self.input || self.input.value) return;
      self.dock.classList.add('gdock--placeholder-fade');
      window.setTimeout(function () {
        i = (i + 1) % PLACEHOLDERS.length;
        self.input.placeholder = PLACEHOLDERS[i];
        self.dock.classList.remove('gdock--placeholder-fade');
      }, 420);
    }, 5200);
  };

  // ------------------------------------------------------------- request

  GAssistant.prototype.ask = function (prompt) {
    var self = this;
    // outfit requests want size-aware sets: capture sizes once, up front
    if (/outfit|wardrobe|dress me|look for/i.test(prompt) && !this.sizes) {
      this.pendingPrompt = prompt;
      this.sizeTray();
      return;
    }
    if (this.abort) this.abort.abort();
    this.abort = new AbortController();
    // surface AI usage to whatever analytics is on the page (GA4/GTM, Meta)
    try {
      if (window.dataLayer) window.dataLayer.push({ event: 'ai_stylist_ask', ai_prompt: prompt.slice(0, 120), ai_anchor: this.anchor || null });
      if (typeof window.fbq === 'function') window.fbq('trackCustom', 'AIStylistAsk', { prompt: prompt.slice(0, 120) });
    } catch (e) { /* analytics unavailable */ }
    this.setLoading(true);
    this.warmAssets();
    this.closeTray();
    this.chips.hidden = true;

    var context = this.journey ? {
      conversation_id: this.journey.conversation_id,
      prompt_history: this.journey.prompts || [],
      product_ids: this.journey.product_ids || []
    } : {};
    if (this.sizes && (this.sizes.top || this.sizes.waist)) context.sizes = this.sizes;
    if (this.anchor) context.anchor_handle = this.anchor;
    if (!Object.keys(context).length) context = null;

    var timeout = window.setTimeout(function () { self.abort.abort(); }, REQUEST_TIMEOUT_MS);

    this.provider(prompt, context, this.abort.signal)
      .then(function (raw) { return self.validate(raw, prompt); })
      .then(function (journey) {
        window.clearTimeout(timeout);
        self.setLoading(false);
        try {
          console.info('[Guillo assistant] source:', self.endpoint ? 'live-backend' : 'dev-provider',
            '| intent:', journey.intent, '| modules:', journey.modules.length);
        } catch (e) { /* logging only */ }
        if (journey.intent === 'direct_navigation' && journey.destination_url) {
          window.location.href = journey.destination_url;
          return;
        }
        if (journey.intent === 'cart_action' && journey.cart) {
          self.cartAction(journey.cart);
          return;
        }
        self.record(journey, prompt);
        self.mount(journey);
      })
      .catch(function (err) {
        window.clearTimeout(timeout);
        self.setLoading(false);
        if (err && err.name === 'AbortError') { /* replaced or timed out */ }
        try { console.info('[Guillo assistant] source: error-fallback |', err && err.message); } catch (e2) { /* noop */ }
        self.fallback(prompt);
      });
  };

  GAssistant.prototype.provider = function (prompt, context, signal) {
    if (this.endpoint) {
      return fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        signal: signal,
        body: JSON.stringify({ prompt: prompt, context: context, outfit_config: this.outfitConfig })
      }).then(function (r) {
        if (!r.ok) throw new Error('backend ' + r.status);
        return r.json();
      });
    }
    if (window.GAssistantDevProvider) {
      // Clearly separated development provider: builds journeys from live
      // storefront data. Not the production AI.
      return window.GAssistantDevProvider(prompt, context, this.assets, signal);
    }
    return Promise.reject(new Error('no provider configured'));
  };

  // ---------------------------------------------------------- validation

  GAssistant.prototype.validate = function (raw, prompt) {
    if (!raw || typeof raw !== 'object' || !Array.isArray(raw.modules)) {
      throw new Error('invalid response shape');
    }
    var self = this;
    var modules = raw.modules
      .filter(function (m) { return m && MODULE_TYPES.indexOf(m.type) !== -1; })
      .slice(0, MAX_MODULES)
      .map(function (m) { return self.validateModule(m); })
      .filter(Boolean);

    if (!modules.length && raw.intent !== 'direct_navigation' && raw.intent !== 'cart_action') {
      throw new Error('no valid modules');
    }

    var productIds = [];
    modules.forEach(function (m) {
      (m.products || []).forEach(function (p) { productIds.push(p.id); });
    });

    return {
      id: 'j' + Date.now().toString(36),
      conversation_id: raw.conversation_id || 'c' + Date.now().toString(36),
      intent: typeof raw.intent === 'string' ? raw.intent.slice(0, 40) : 'discovery',
      destination_url: this.safeUrl(raw.destination_url),
      cart: raw.cart || null,
      headline: clampText(raw.headline),
      modules: modules,
      product_ids: productIds,
      prompts: [prompt],
      follow_up_prompts: (Array.isArray(raw.follow_up_prompts) ? raw.follow_up_prompts : [])
        .filter(function (p) { return typeof p === 'string'; })
        .map(clampText)
        .slice(0, MAX_FOLLOW_UPS)
    };
  };

  GAssistant.prototype.validateModule = function (m) {
    var out = { type: m.type, id: String(m.id || '').slice(0, 40) };
    out.headline = clampText(m.headline);
    out.supporting_text = clampText(m.supporting_text);
    out.action_label = clampText(m.action_label);
    out.action_url = this.safeUrl(m.action_url);
    out.body_text = typeof m.body_text === 'string' ? m.body_text.slice(0, 600) : '';

    // asset ids must exist in the approved registry
    if (m.asset_id && this.assets[m.asset_id]) out.asset = this.assets[m.asset_id];
    if (m.background_asset_id && this.assets[m.background_asset_id]) {
      out.background_asset = this.assets[m.background_asset_id];
    }

    // products: verified objects with url/price/availability (populated by
    // the provider from live storefront data; re-checked here)
    out.products = (Array.isArray(m.products) ? m.products : [])
      .filter(function (p) {
        return p && typeof p.url === 'string' && p.url.indexOf('/products/') === 0 &&
          typeof p.title === 'string' && typeof p.price === 'number';
      })
      .slice(0, 9)
      .map(function (p) {
        return {
          id: String(p.id || ''), handle: String(p.handle || ''),
          title: p.title.slice(0, 80), url: p.url,
          price: p.price, compare_at_price: typeof p.compare_at_price === 'number' ? p.compare_at_price : null,
          image: typeof p.image === 'string' && /^(https:)?\/\//.test(p.image) ? p.image : '',
          available: p.available !== false,
          variants: Array.isArray(p.variants) ? p.variants.slice(0, 30) : [],
          options: Array.isArray(p.options) ? p.options : [],
          colour: typeof p.colour === 'string' ? p.colour.slice(0, 40) : ''
        };
      })
      .filter(function (p) { return p.available; });

    out.recommendations = (Array.isArray(m.recommendations) ? m.recommendations : [])
      .slice(0, MAX_RECOMMENDATIONS)
      .map(function (r) {
        return {
          title: clampText(r.title),
          prompt: clampText(r.prompt || r.title),
          image: typeof r.image === 'string' && /^(https:)?\/\//.test(r.image) ? r.image : ''
        };
      })
      .filter(function (r) { return r.title; });

    // minimum content requirements per type
    if (out.type === 'product_grid_three_column' && out.products.length < 2) return null;
    if ((out.type === 'product_focus' || out.type === 'product_detail') && !out.products.length) return null;
    if (out.type === 'campaign_full_bleed' && !out.asset && !out.products.length) return null;
    return out;
  };

  GAssistant.prototype.safeUrl = function (u) {
    if (typeof u !== 'string') return '';
    if (u.indexOf('/') === 0 && u.indexOf('//') !== 0) return u; // same-origin paths only
    return '';
  };

  // ------------------------------------------------------------ renderer

  GAssistant.prototype.mount = function (journey) {
    var self = this;
    this.journey = journey;
    this.stage.innerHTML = '';

    // the first screen must be shoppable: intros preview the journey's products
    var allProducts = [];
    journey.modules.forEach(function (m) {
      (m.products || []).forEach(function (p) {
        if (!allProducts.some(function (x) { return x.url === p.url; })) allProducts.push(p);
      });
    });
    journey.modules.forEach(function (m) {
      if (m.type === 'editorial_result_intro' && !(m.products || []).length) {
        m._preview = allProducts.slice(0, 3);
      }
    });

    // outfit journeys: multiple complete looks become one board on screen one
    var looks = journey.modules.filter(function (m) { return m.type === 'complete_look' && m.products.length >= 2; });
    var renderList = journey.modules;
    if (looks.length >= 2) {
      renderList = [{ type: '_outfit_board', id: 'board', headline: journey.headline || 'Build your outfit', _looks: looks }]
        .concat(journey.modules.filter(function (m) { return looks.indexOf(m) === -1 && m.type !== 'editorial_result_intro'; }));
    }

    renderList.forEach(function (m) {
      var scene = self.renderModule(m);
      if (scene) {
        var slot = el('div', 'gscene-slot');
        slot.appendChild(scene);
        self.stage.appendChild(slot);
      }
    });

    this.journeyEl.hidden = false;
    if (this.shortcuts) this.shortcuts.hidden = true;
    this.renderJourneySizes();
    if (window.__gscenes && window.__gscenes.enterJourney) {
      window.__gscenes.enterJourney(this.stage);
    }
    this.announce(journey.headline || 'Your selection is ready');
    this.syncSceneContext(0, this.stage.firstChild);
  };

  GAssistant.prototype.renderModule = function (m) {
    switch (m.type) {
      case '_outfit_board': return this.modOutfitBoard(m);
      case 'editorial_result_intro': return this.modIntro(m);
      case 'product_grid_three_column': return this.modGrid(m);
      case 'campaign_full_bleed': return this.modBleed(m);
      case 'complete_look':
        // with campaign media it is a full-bleed look; without, show the
        // pairing as side-by-side pieces so the scene is never empty
        return (m.asset || m.background_asset) ? this.modBleed(m, true) : this.modGrid(m, m.headline || 'The complete look');
      case 'product_focus':
      case 'product_detail': return this.modFocus(m);
      case 'product_comparison': return this.modGrid(m, 'Compared for you');
      case 'policy_answer': return this.modPolicy(m);
      case 'fallback': return this.modFallback(m);
      default: return null;
    }
  };

  // Right-size a registry image URL for the current screen so journeys are
  // not decoding 2200px images on phones.
  GAssistant.prototype.assetUrl = function (url) {
    if (window.innerWidth <= 900 && /([?&])width=\d+/.test(url)) {
      return url.replace(/([?&])width=\d+/, '$1width=1100');
    }
    return url;
  };

  // Begin fetching the most likely journey backdrop while the provider is
  // still answering, so the journey opens onto a decoded image.
  GAssistant.prototype.warmAssets = function () {
    if (this.__warmed) return;
    this.__warmed = true;
    var best = this.pickAsset(['ai', 'campaign', 'lifestyle']);
    if (best && best.kind === 'image' && best.url) {
      var i = new Image();
      i.src = this.assetUrl(best.url);
    }
  };

  GAssistant.prototype.media = function (asset, cls) {
    var wrap = el('div', 'gs-media ' + (cls || ''));
    if (!asset) return wrap;
    if (asset.kind === 'video' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      var v = document.createElement('video');
      v.muted = true; v.loop = true; v.playsInline = true; v.setAttribute('playsinline', '');
      v.setAttribute('data-gscene-video', ''); v.preload = 'none'; v.tabIndex = -1;
      var s = document.createElement('source');
      s.setAttribute('data-src', asset.url); s.type = 'video/mp4';
      v.appendChild(s);
      wrap.classList.add('gs-media--ready');
      wrap.appendChild(v);
    } else if (asset.url) {
      var img = document.createElement('img');
      img.alt = ''; img.decoding = 'async';
      img.setAttribute('fetchpriority', 'high');
      img.src = this.assetUrl(asset.url);
      // fade in only once decoded so the image never paints top-down on screen
      var reveal = function () { wrap.classList.add('gs-media--ready'); };
      if (img.complete && img.naturalWidth) reveal();
      else {
        img.addEventListener('load', function () {
          if (img.decode) img.decode().then(reveal).catch(reveal);
          else reveal();
        }, { once: true });
        img.addEventListener('error', reveal, { once: true });
        // never leave a scene permanently blank on a stalled connection
        window.setTimeout(reveal, 2500);
      }
      wrap.appendChild(img);
    }
    var ov = el('span', 'gs-media__overlay');
    ov.style.opacity = 0.25;
    wrap.appendChild(ov);
    return wrap;
  };

  GAssistant.prototype.pickAsset = function (tags) {
    var best = null;
    var assets = this.assets;
    Object.keys(assets).forEach(function (id) {
      var score = (assets[id].tags || []).filter(function (t) { return tags.indexOf(t) !== -1; }).length;
      if (assets[id].tags && assets[id].tags.indexOf('ai') !== -1) score += 3; // merchant-chosen AI backdrop wins
      if (!best || score > best.score) best = { asset: assets[id], score: score };
    });
    return best ? best.asset : null;
  };

  GAssistant.prototype.modIntro = function (m) {
    var self = this;
    var scene = el('div', 'gscene gmod--intro');
    scene.setAttribute('data-gscene', '');
    scene.setAttribute('data-gheader', 'light');
    // the intro must always have atmosphere: fall back to an approved asset
    var bg = m.background_asset || m.asset || this.pickAsset(['ai', 'campaign', 'lifestyle', this.journeyTagHint || '']);
    scene.appendChild(this.media(bg));
    var c = el('div', 'gscene__content');
    if (m.headline) c.appendChild(el('h2', 'gs-heading', m.headline));
    if (m.supporting_text) c.appendChild(el('p', 'gs-sub', m.supporting_text));
    // shoppable preview cards directly on the first screen
    if (m._preview && m._preview.length) {
      var prev = el('div', 'gmod__preview');
      m._preview.forEach(function (p) {
        var card = el('a', 'gmod__preview-card');
        card.href = p.url;
        if (p.image) {
          var img = document.createElement('img');
          img.src = p.image; img.alt = p.title; img.loading = 'lazy';
          card.appendChild(img);
        }
        var meta = el('span', 'gmod__preview-meta');
        meta.appendChild(el('span', '', p.title));
        meta.appendChild(el('span', 'price', money(p.price)));
        card.appendChild(meta);
        prev.appendChild(card);
      });
      c.appendChild(prev);
    }
    // directions also live on the scene itself, not only in the tray
    if (m.recommendations.length) {
      var row = el('div', 'gmod__routes gmod__routes--intro');
      m.recommendations.forEach(function (r) {
        var b = el('button', 'gs-pill', r.title);
        b.type = 'button';
        b.addEventListener('click', function () { self.ask(r.prompt); });
        row.appendChild(b);
      });
      c.appendChild(row);
    }
    scene.appendChild(c);
    if (m.recommendations.length) this.choiceTray(m.recommendations);
    return scene;
  };

  GAssistant.prototype.modOutfitBoard = function (m) {
    var self = this;
    var scene = el('div', 'gscene gmod--intro gmod--board');
    scene.setAttribute('data-gscene', '');
    scene.setAttribute('data-gheader', 'light');
    scene.appendChild(this.media(this.pickAsset(['ai', 'campaign', 'lifestyle'])));
    var c = el('div', 'gscene__content gboard');
    c.appendChild(el('h2', 'gs-heading', m.headline || 'Build your outfit'));
    var lastPrompt = (this.journey && this.journey.prompts && this.journey.prompts.slice(-1)[0]) || '';
    if (lastPrompt) {
      var sub = el('p', 'gboard__sub', 'Curated looks for your prompt: ');
      sub.appendChild(el('em', '', lastPrompt));
      c.appendChild(sub);
    }

    // desktop wheel scrolling inside the board hands back to the stage at
    // the edges (data-gscene-vscroll, handled by the scene controller)
    c.setAttribute('data-gscene-vscroll', '');

    m._looks.slice(0, 4).forEach(function (look) {
      var row = el('div', 'gboard__row');

      // LEFT: thumbnail strip (max 4 tiles; overflow collapses into +N)
      var mediaCol = el('div', 'gboard__media-col');
      var cards = el('div', 'gboard__cards');
      var total = 0;
      look.products.forEach(function (p) { total += p.price; });
      var visible = look.products.length > 4 ? look.products.slice(0, 3) : look.products;
      visible.forEach(function (p) {
        var card = el('a', 'gboard__card');
        card.href = p.url;
        if (p.image) {
          var img = document.createElement('img');
          img.src = p.image; img.alt = p.title; img.loading = 'lazy';
          card.appendChild(img);
        }
        card.appendChild(el('span', 'gboard__card-title', p.title));
        cards.appendChild(card);
      });
      if (look.products.length > 4) {
        var moreTile = el('button', 'gboard__more', '+' + (look.products.length - 3));
        moreTile.type = 'button';
        moreTile.setAttribute('aria-label', 'View all pieces in this look');
        moreTile.addEventListener('click', function () { self.outfitTray(look); });
        cards.appendChild(moreTile);
      }
      mediaCol.appendChild(cards);
      mediaCol.appendChild(el('span', 'gboard__count', look.products.length + (look.products.length === 1 ? ' item' : ' items')));
      row.appendChild(mediaCol);

      // CENTRE: look title, reason, edit link
      var mid = el('div', 'gboard__mid');
      if (look.headline) mid.appendChild(el('span', 'gboard__name', look.headline));
      if (look.supporting_text) mid.appendChild(el('span', 'gboard__desc', look.supporting_text));
      var links = el('div', 'gboard__links');
      var editLink = el('button', '', 'Edit items');
      editLink.type = 'button';
      editLink.addEventListener('click', function () { self.outfitTray(look); });
      links.appendChild(editLink);
      mid.appendChild(links);
      row.appendChild(mid);

      // RIGHT: pricing + CTA
      var side = el('div', 'gboard__side');
      var offer = self.offer;
      if (offer.on && look.products.length >= offer.min) {
        side.appendChild(el('span', 'gboard__offer', offer.pct + '% savings'));
        var priceRow = el('span', 'gboard__total');
        var was = el('s', 'gboard__was', money(total));
        priceRow.appendChild(was);
        priceRow.appendChild(document.createTextNode(' ' + money(Math.round(total * (100 - offer.pct) / 100))));
        side.appendChild(priceRow);
        side.appendChild(el('span', 'gboard__offer-note', 'Applied at checkout'));
      } else {
        side.appendChild(el('span', 'gboard__total', money(total)));
      }
      var btn = el('button', 'gboard__add', 'Add all to bag');
      btn.type = 'button';
      btn.addEventListener('click', function () { self.outfitTray(look); });
      side.appendChild(btn);
      row.appendChild(side);
      c.appendChild(row);
    });

    // refinement chips: the AI's follow-ups plus two evergreen directions
    var refinePrompts = this.currentFollowUps().slice(0, 4);
    ['Neutral tones', 'Under $250'].forEach(function (x) {
      if (refinePrompts.length < 6 && refinePrompts.indexOf(x) === -1) refinePrompts.push(x);
    });
    if (refinePrompts.length) {
      var refine = el('div', 'gboard__refine');
      refinePrompts.forEach(function (p) {
        var b = el('button', '', p);
        b.type = 'button';
        b.addEventListener('click', function () { self.input.value = p; self.ask(p); });
        refine.appendChild(b);
      });
      c.appendChild(refine);
    }

    this.accessoriesRail(c);
    scene.appendChild(c);
    return scene;
  };

  // "Complete it with" rail under the outfit board: accessories that take a
  // 2-piece look to the 3+ pieces the wardrobe offer needs. Only available
  // products render (each is checked against the live storefront).
  GAssistant.prototype.accessoriesRail = function (container) {
    var self = this;
    var HANDLES = ['the-genova-belt', 'the-bellagio-wash-bag', 'the-bellagio-passport-holder', 'bellagio-leather-weekender-duffel-bag'];
    var rail = el('div', 'gboard__acc');
    rail.hidden = true; // revealed when the first available accessory resolves
    var head = el('p', 'gboard__acc-head', 'Complete it with');
    if (this.offer.on) {
      head.appendChild(el('span', 'gboard__acc-offer', this.offer.min + '+ pieces saves ' + this.offer.pct + '%'));
    }
    rail.appendChild(head);
    var strip = el('div', 'gboard__acc-strip');
    rail.appendChild(strip);
    container.appendChild(rail);

    HANDLES.forEach(function (h) {
      fetch('/products/' + h + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          if (!p) return;
          var v = (p.variants || []).filter(function (x) { return x.available; })[0];
          if (!v) return;
          var card = el('div', 'gboard__acc-card');
          var a = el('a', 'gboard__acc-link');
          a.href = '/products/' + p.handle;
          if (p.featured_image) {
            var img = document.createElement('img');
            img.src = p.featured_image;
            img.alt = p.title;
            img.loading = 'lazy';
            a.appendChild(img);
          }
          a.appendChild(el('span', 'gboard__acc-title', p.title));
          a.appendChild(el('span', 'gboard__acc-price', money(p.price)));
          card.appendChild(a);
          var add = el('button', 'gboard__acc-add', 'Add');
          add.type = 'button';
          add.addEventListener('click', function () {
            add.disabled = true;
            add.textContent = 'Adding…';
            if (window.GuilloCart && window.GuilloCart.add) {
              window.GuilloCart.add(v.id, 1).then(function () {
                add.textContent = 'Added ✓';
                self.announce(p.title + ' added to your bag');
                window.setTimeout(function () { add.disabled = false; add.textContent = 'Add'; }, 1800);
              }).catch(function () {
                add.disabled = false;
                add.textContent = 'Try again';
              });
            } else {
              window.location.href = '/cart/add?id=' + v.id;
            }
          });
          card.appendChild(add);
          strip.appendChild(card);
          rail.hidden = false;
        })
        .catch(function () { /* accessory unavailable: skip */ });
    });
  };

  // size selection for a whole outfit, then sequential adds via the real cart
  GAssistant.prototype.outfitTray = function (look) {
    var self = this;
    this.trayBody.innerHTML = '';
    this.trayBody.appendChild(el('p', 'gtray__title', 'Your look · choose sizes'));
    var chosen = {};
    var included = {};

    function sizedOf(p) { return p.variants && p.variants.length > 1; }
    function refresh() {
      var anyIn = look.products.some(function (p) { return included[p.url]; });
      var missing = look.products.some(function (p) {
        return included[p.url] && sizedOf(p) && !chosen[p.url];
      });
      confirmBtn.disabled = !anyIn || missing;
    }

    look.products.forEach(function (p) {
      included[p.url] = true;
      var sized = sizedOf(p);
      var box = el('div', 'gtray__outfit-item');
      var head = el('p', 'gtray__outfit-name', p.title + (sized ? '' : ' (one size)'));
      // customers can trim the look: drop the belt, keep the rest
      var toggle = el('button', 'gtray__include', 'Remove');
      toggle.type = 'button';
      toggle.setAttribute('aria-pressed', 'true');
      toggle.addEventListener('click', function () {
        included[p.url] = !included[p.url];
        toggle.textContent = included[p.url] ? 'Remove' : 'Add back';
        toggle.setAttribute('aria-pressed', String(included[p.url]));
        box.classList.toggle('gtray__outfit-item--off', !included[p.url]);
        refresh();
      });
      head.appendChild(toggle);
      box.appendChild(head);
      if (!sized) {
        chosen[p.url] = p.variants && p.variants[0] ? p.variants[0].id : null;
        return self.trayBody.appendChild(box);
      }
      var lettered = p.variants.some(function (v) { return /^[a-zA-Z]+$/.test(normSize(v.title)); });
      var saved = self.sizes ? normSize(lettered ? self.sizes.top : self.sizes.waist) : '';
      var sizes = el('div', 'gtray__sizes');
      p.variants.forEach(function (v) {
        var b = el('button', '', v.title);
        b.type = 'button';
        b.disabled = !v.available;
        var pre = saved && v.available && normSize(v.title) === saved;
        if (pre) { chosen[p.url] = v.id; }
        b.setAttribute('aria-pressed', pre ? 'true' : 'false');
        b.addEventListener('click', function () {
          sizes.querySelectorAll('button').forEach(function (x) { x.setAttribute('aria-pressed', 'false'); });
          b.setAttribute('aria-pressed', 'true');
          chosen[p.url] = v.id;
          refresh();
        });
        sizes.appendChild(b);
      });
      box.appendChild(sizes);
      self.trayBody.appendChild(box);
    });

    var actions = el('div', 'gtray__actions');
    var confirmBtn = el('button', 'gtray__atc', 'Add outfit to bag');
    confirmBtn.type = 'button';
    refresh();
    confirmBtn.addEventListener('click', function () {
      confirmBtn.disabled = true;
      confirmBtn.textContent = 'Adding…';
      var ids = look.products
        .filter(function (p) { return included[p.url]; })
        .map(function (p) { return chosen[p.url]; })
        .filter(Boolean);
      var addNext = function (i) {
        if (i >= ids.length) {
          self.announce('Outfit added to your bag');
          self.closeTray();
          if (window.GuilloCart && window.GuilloCart.open) window.GuilloCart.open();
          fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
            document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: cart }, bubbles: true }));
          });
          return;
        }
        var add = window.GuilloCart && window.GuilloCart.add
          ? Promise.resolve(window.GuilloCart.add(ids[i], 1))
          : fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: ids[i], quantity: 1 }) });
        Promise.resolve(add).then(function () { addNext(i + 1); })
          .catch(function () { confirmBtn.disabled = false; confirmBtn.textContent = 'Add outfit to bag'; self.announce('We could not add that just now.'); });
      };
      addNext(0);
    });
    actions.appendChild(confirmBtn);
    this.trayBody.appendChild(actions);
    this.openTray();
  };

  GAssistant.prototype.modGrid = function (m, label) {
    var scene = el('div', 'gscene gmod--grid');
    scene.setAttribute('data-gscene', '');
    scene.setAttribute('data-gheader', 'dark');
    scene.appendChild(el('p', 'gmod__label', m.headline || label || 'Selected for you'));
    var row = el('div', 'gmod__cards');
    row.setAttribute('data-gscene-hscroll', '');
    m.products.slice(0, 3).forEach(function (p) {
      var a = el('a', 'gmod__card');
      a.href = p.url;
      var mediaBox = el('span', 'gmod__card-media');
      if (p.image) {
        var img = document.createElement('img');
        img.src = p.image; img.alt = p.title; img.loading = 'lazy';
        mediaBox.appendChild(img);
      }
      a.appendChild(mediaBox);
      var meta = el('span', 'gmod__card-meta');
      meta.appendChild(el('span', 'title', p.title));
      meta.appendChild(el('span', 'price', money(p.price)));
      a.appendChild(meta);
      row.appendChild(a);
    });
    scene.appendChild(row);
    return scene;
  };

  GAssistant.prototype.modBleed = function (m, isLook) {
    var scene = el('div', 'gscene gmod--bleed');
    scene.setAttribute('data-gscene', '');
    scene.setAttribute('data-gheader', 'light');
    scene.appendChild(this.media(m.asset || m.background_asset));
    if (m.action_label && (m.products.length || m.action_url)) {
      var pillRow = el('div', 'gmod__pill-row');
      var pill = el(m.action_url ? 'a' : 'button', 'gs-pill', m.action_label);
      if (m.action_url) pill.href = m.action_url;
      else {
        pill.type = 'button';
        pill.setAttribute('data-gmod-look', '');
        scene._lookProducts = m.products;
      }
      pillRow.appendChild(pill);
      scene.appendChild(pillRow);
    }
    if (isLook && m.products.length) scene._lookProducts = m.products;
    return scene;
  };

  GAssistant.prototype.modFocus = function (m) {
    var p = m.products[0];
    var scene = el('div', 'gscene gmod--focus');
    scene.setAttribute('data-gscene', '');
    scene.setAttribute('data-gheader', 'dark');
    // merchant-set AI backdrop applies here too (softly blurred behind)
    var bg = this.pickAsset(['ai']);
    if (bg && (bg.tags || []).indexOf('ai') !== -1) {
      scene.classList.add('gmod--focus--backdrop');
      scene.setAttribute('data-gheader', 'light');
      scene.appendChild(this.media(bg));
    }
    scene.appendChild(el('h2', 'gmod__focus-title', m.headline || p.title));
    var mediaBox = el('div', 'gmod__focus-media');
    if (p.image) {
      var img = document.createElement('img');
      img.src = p.image; img.alt = p.title;
      mediaBox.appendChild(img);
    }
    scene.appendChild(mediaBox);
    scene._detailProduct = p;
    var self = this;
    window.setTimeout(function () { if (self.journey) self.productTray(p); }, 400);
    return scene;
  };

  GAssistant.prototype.modPolicy = function (m) {
    var scene = el('div', 'gscene gmod--policy');
    scene.setAttribute('data-gscene', '');
    scene.setAttribute('data-gheader', 'dark');
    var c = el('div', 'gscene__content');
    c.appendChild(el('h2', 'gs-heading', m.headline || 'Here to help'));
    if (m.body_text) c.appendChild(el('p', '', m.body_text));
    if (m.action_url && m.action_label) {
      var a = el('a', 'gs-pill gs-pill--ink', m.action_label);
      a.href = m.action_url;
      c.appendChild(a);
    }
    scene.appendChild(c);
    return scene;
  };

  GAssistant.prototype.modFallback = function (m) {
    var scene = this.modPolicy(m);
    scene.classList.add('gmod--fallback');
    var routes = el('div', 'gmod__routes');
    [['New arrivals', '/collections/new-arrivals'], ['Chinos', '/collections/chinos'],
     ['Best sellers', '/collections/best-sellers'], ['Search', '/search']].forEach(function (r) {
      var a = el('a', '', r[0]); a.href = r[1]; routes.appendChild(a);
    });
    scene.querySelector('.gscene__content').appendChild(routes);
    return scene;
  };

  // ------------------------------------------------------------- trays

  GAssistant.prototype.openTray = function () { this.tray.hidden = false; };
  GAssistant.prototype.closeTray = function () {
    this.tray.hidden = true;
    this.tray.classList.remove('gdock__tray--side');
    this.trayBody.innerHTML = '';
  };

  GAssistant.prototype.choiceTray = function (recs) {
    var self = this;
    this.trayBody.innerHTML = '';
    this.trayBody.appendChild(el('p', 'gtray__title', 'Choose a direction'));
    var row = el('div', 'gtray__choices');
    recs.forEach(function (r) {
      var b = el('button', 'gtray__choice');
      b.type = 'button';
      if (r.image) {
        var img = document.createElement('img');
        img.src = r.image; img.alt = '';
        b.appendChild(img);
      }
      b.appendChild(el('span', '', r.title));
      b.addEventListener('click', function () { self.ask(r.prompt); });
      row.appendChild(b);
    });
    this.trayBody.appendChild(row);
    this.openTray();
  };

  GAssistant.prototype.lookTray = function (products) {
    this.trayBody.innerHTML = '';
    this.trayBody.appendChild(el('p', 'gtray__title', 'The look'));
    var row = el('div', 'gtray__thumbs');
    products.slice(0, 5).forEach(function (p) {
      var a = el('a', 'gtray__thumb');
      a.href = p.url;
      if (p.image) {
        var img = document.createElement('img');
        img.src = p.image; img.alt = p.title;
        a.appendChild(img);
      }
      a.appendChild(el('span', 'gtray__thumb-title', p.title));
      a.appendChild(el('span', 'gtray__thumb-price', money(p.price)));
      row.appendChild(a);
    });
    this.trayBody.appendChild(row);
    this.openTray();
  };

  GAssistant.prototype.productTray = function (p, note) {
    var self = this;
    this.trayBody.innerHTML = '';
    var box = el('div', 'gtray__product');
    var imgBox = el('div', 'gtray__product-img');
    if (p.image) {
      var img = document.createElement('img');
      img.src = p.image; img.alt = p.title;
      imgBox.appendChild(img);
    }
    box.appendChild(imgBox);

    var info = el('div', 'gtray__product-info');
    info.appendChild(el('h3', 'gtray__product-title', p.title));
    info.appendChild(el('p', 'gtray__product-price', money(p.price)));
    if (p.colour) info.appendChild(el('p', 'gtray__product-colour', p.colour));

    var selected = null;
    var sizeIdx = -1;
    (p.options || []).forEach(function (o, i) {
      if (/size|waist/i.test(o.name || o)) sizeIdx = i;
    });
    var sizes = el('div', 'gtray__sizes');
    sizes.setAttribute('role', 'group');
    sizes.setAttribute('aria-label', 'Select size');
    var seen = {};
    (p.variants || []).forEach(function (v) {
      var label = sizeIdx >= 0 ? v.options[sizeIdx] : v.title;
      if (!label || seen[label]) return;
      seen[label] = true;
      var b = el('button', '', label);
      b.type = 'button';
      b.disabled = !v.available;
      b.setAttribute('aria-pressed', 'false');
      b.addEventListener('click', function () {
        sizes.querySelectorAll('button').forEach(function (x) { x.setAttribute('aria-pressed', 'false'); });
        b.setAttribute('aria-pressed', 'true');
        selected = v;
        atc.disabled = false;
      });
      sizes.appendChild(b);
    });
    if (sizes.children.length) info.appendChild(sizes);

    var actions = el('div', 'gtray__actions');
    var atc = el('button', 'gtray__atc', 'Add to bag');
    atc.type = 'button';
    atc.disabled = sizes.children.length > 0;
    atc.addEventListener('click', function () {
      var variant = selected || (p.variants || []).filter(function (v) { return v.available; })[0];
      if (!variant) return;
      self.cartAction({ variant_id: variant.id, quantity: 1, title: p.title });
    });
    actions.appendChild(atc);
    var link = el('a', 'gtray__link', 'Discover the product');
    link.href = p.url;
    actions.appendChild(link);
    info.appendChild(actions);
    if (note) info.appendChild(el('p', 'gtray__note', note));
    box.appendChild(info);
    this.trayBody.appendChild(box);
    this.openTray();
  };

  GAssistant.prototype.onStageClick = function (e) {
    var lookBtn = e.target.closest('[data-gmod-look]');
    if (lookBtn) {
      var scene = lookBtn.closest('.gscene');
      if (scene && scene._lookProducts) this.lookTray(scene._lookProducts);
    }
  };

  // --------------------------------------------------------------- cart

  GAssistant.prototype.cartAction = function (cart) {
    var self = this;
    if (!cart || !cart.variant_id) return;
    var add = function () {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: cart.variant_id, quantity: cart.quantity || 1 })
      }).then(function (r) {
        if (!r.ok) throw new Error('cart');
        return r.json();
      });
    };
    var done = function () {
      self.announce((cart.title || 'Item') + ' added to your bag');
      self.closeTray();
      if (window.GuilloCart && window.GuilloCart.open) {
        if (window.GuilloCart.refresh) window.GuilloCart.refresh();
        window.GuilloCart.open();
      } else {
        var t = document.querySelector('[data-drawer-toggle="drawer-cart"]');
        if (t) t.click();
      }
      fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
        document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: c }, bubbles: true }));
      });
    };
    if (window.GuilloCart && window.GuilloCart.add) {
      Promise.resolve(window.GuilloCart.add(cart.variant_id, cart.quantity || 1)).then(done)
        .catch(function () { add().then(done).catch(function () { self.announce('We could not add that just now.'); }); });
    } else {
      add().then(done).catch(function () { self.announce('We could not add that just now.'); });
    }
  };

  // ---------------------------------------------------- loading & errors

  GAssistant.prototype.setLoading = function (on) {
    var self = this;
    this.loading.hidden = !on;
    this.dock.classList.toggle('gdock--loading', on);
    $('.gdock__spinner', this.root).hidden = !on;
    if (this.shortcuts) {
      this.shortcuts.hidden = on || !!(this.journey && this.journeyEl && !this.journeyEl.hidden);
    }
    if (window.__gscenes) window.__gscenes.overlayCount += on ? 1 : -1;
    if (on) {
      var i = 0;
      var status = $('[data-gloading-status]', this.root);
      status.textContent = LOADING_LINES[0];
      this.announce(LOADING_LINES[0]);
      this.loadTimer = window.setInterval(function () {
        i = (i + 1) % LOADING_LINES.length;
        status.textContent = LOADING_LINES[i];
      }, 2600);
    } else {
      window.clearInterval(this.loadTimer);
      if (window.__gscenes) window.__gscenes.overlayCount = Math.max(0, window.__gscenes.overlayCount);
    }
  };

  GAssistant.prototype.fallback = function (prompt) {
    var journey = {
      id: 'j' + Date.now().toString(36),
      conversation_id: 'c' + Date.now().toString(36),
      intent: 'fallback',
      headline: 'We couldn’t build that selection just now.',
      modules: [{
        type: 'fallback', id: 'fb', headline: 'We couldn’t build that selection just now.',
        supporting_text: '', body_text: 'Try one of these instead.',
        action_label: '', action_url: '', products: [], recommendations: []
      }],
      product_ids: [], prompts: [prompt],
      follow_up_prompts: ['Show me what’s new', 'Show me chinos', 'Best sellers']
    };
    this.mount(journey);
    this.announce(journey.headline);
  };

  // ------------------------------------------------------ journey/history

  GAssistant.prototype.record = function (journey, prompt) {
    if (this.journey && journey.conversation_id === this.journey.conversation_id) {
      journey.prompts = (this.journey.prompts || []).concat([prompt]);
    }
    this.journeys.unshift({
      id: journey.id,
      conversation_id: journey.conversation_id,
      intent: journey.intent,
      headline: journey.headline,
      modules: journey.modules,
      product_ids: journey.product_ids,
      prompts: journey.prompts,
      follow_up_prompts: journey.follow_up_prompts,
      at: Date.now()
    });
    this.journeys = this.journeys.slice(0, 8);
    this.save();
  };

  GAssistant.prototype.closeJourney = function () {
    this.journey = null;
    this.closeTray();
    this.journeyEl.hidden = true;
    if (this.shortcuts) this.shortcuts.hidden = false;
    this.stage.innerHTML = ''; // release journey media
    if (window.__gscenes && window.__gscenes.exitJourney) window.__gscenes.exitJourney();
    this.syncSceneContext(window.__gscenes ? window.__gscenes.current : 0);
    this.announce('Returned to the homepage');
  };

  GAssistant.prototype.renderHistory = function () {
    var list = $('[data-ghistory-list]', this.root);
    list.innerHTML = '';
    if (!this.journeys.length) {
      list.appendChild(el('li', 'ghistory__empty', 'No journeys yet. Ask for anything above.'));
      return;
    }
    this.journeys.forEach(function (j) {
      var li = el('li');
      var b = el('button');
      b.setAttribute('data-journey-id', j.id);
      b.appendChild(el('span', '', (j.prompts && j.prompts[j.prompts.length - 1]) || j.headline || j.intent));
      var d = new Date(j.at);
      b.appendChild(el('small', '', d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })));
      li.appendChild(b);
      list.appendChild(li);
    });
  };

  GAssistant.prototype.load = function () {
    try { return JSON.parse(window.sessionStorage.getItem('gassistant-journeys')) || []; }
    catch (e) { return []; }
  };
  GAssistant.prototype.save = function () {
    try { window.sessionStorage.setItem('gassistant-journeys', JSON.stringify(this.journeys)); }
    catch (e) { /* storage unavailable */ }
  };

  GAssistant.prototype.announce = function (text) { this.sr.textContent = text; };

  // ---------------------------------------------------------------- boot

  function boot() {
    if (!document.querySelector('[data-gassistant-root]')) return;
    if (window.__gassistant) return;
    window.__gassistant = new GAssistant();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
